import './WizardSteps.css';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import constants from '../../../constants/constants';
import SelectPlayerSection from './SelectPlayerSection/SelectPlayerSection';
import SelectcourtSection from './SelectCourtSection/SelectCourtSection';
import SelectDateSection from './SelectDateSection/SelectDateSection';
import MatchScoreSection from './MatchScoreSection/MatchScoreSection';
import SetScoreSection from './SetScoreSection/SetScoreSection';
import { useDispatch } from 'react-redux';
import * as matchActions from '../../../redux/actions/matchActions';

const rtlTheme = createTheme({ direction: 'rtl' });
const rtlCache = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

function WizardSteps() {
    const [player, setPlayer] = useState({ id: '' });
    const [courtId, setCourtId] = useState('');
    const [date, setDate] = useState(new Date());
    const [playerOneScore, setPlayerOneScore] = useState('');
    const [playerTwoScore, setPlayerTwoScore] = useState('');
    const [setScore, setSetScore] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const [isFinished, setIsFinished] = useState(false);

    const { player: playerInContext, playersList } = useSelector((state) => state.player);
    const { isGuest } = useSelector((state) => state.auth);
    const { isLoading, error } = useSelector((state) => state.match);
    const { courts } = useSelector((state) => state.court);

    const dispatch = useDispatch();

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleFinish = async (submittedSetScore, wasTooHot = false) => {
        setSetScore(submittedSetScore);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);

        const payload = {
            playerOneId: playerInContext.playerId,
            playerTwoId: player.id,
            courtId: courtId,
            date: date,
            playerOneScore: playerOneScore,
            playerTwoScore: playerTwoScore,
            setScore: submittedSetScore,
            wasTooHot: wasTooHot,
        };

        dispatch(matchActions.addMatch(payload));
        setIsFinished(true);
    };

    const courtSelectedIdChanged = (courtId) => {
        setCourtId(courtId);
    };

    const matchScoreChanged = (value) => {
        if (value.name === 'playerOneGroup') {
            setPlayerOneScore(value.value);
        } else if (value.name === 'playerTwoGroup') {
            setPlayerTwoScore(value.value);
        }
    };

    const playerSelectedIdChanged = (player) => {
        setPlayer(player);
    };

    const dateSelectedChanged = (selectedDate) => {
        setDate(selectedDate);
    };

    const getStepSummary = (stepIndex) => {
        if (activeStep <= stepIndex) return null;

        switch (stepIndex) {
            case 0: {
                if (!player?.firstName) return null;
                return `${player.firstName} ${player.lastName}`;
            }
            case 1: {
                if (!courtId) return null;
                const selectedCourt = courts.find((_, index) => (index + 1).toString() === courtId);
                return selectedCourt ? selectedCourt.name : null;
            }
            case 2: {
                if (!date) return null;
                const d = new Date(date);
                return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
            }
            case 3: {
                if (playerOneScore === '' || playerTwoScore === '' || !playerInContext?.firstName || !player?.firstName) return null;
                return `${playerInContext.firstName} ${playerOneScore} - ${playerTwoScore} ${player.firstName}`;
            }
            case 4: {
                const numberOfSets = parseInt(playerOneScore) + parseInt(playerTwoScore);
                if (!numberOfSets || Object.keys(setScore).length < numberOfSets * 2) return null;
                const parts = [];
                for (let i = 1; i <= numberOfSets; i++) {
                    const p1 = setScore[`player-one-set-${i}`]?.value;
                    const p2 = setScore[`player-two-set-${i}`]?.value;
                    if (p1 !== undefined && p2 !== undefined) parts.push({ set: i, score: `${p2}-${p1}` });
                }
                if (!parts.length) return null;
                return (
                    <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                        {parts.map(({ set, score }) => (
                            <span key={set} dir='ltr'>
                                {`S${set}: ${playerInContext?.firstName} ${score} ${player?.firstName}`}
                            </span>
                        ))}
                    </span>
                );
            }
            default:
                return null;
        }
    };

    const steps = [
        {
            label: constants.ADD_GAME.SELECT_PLAYERL,
            description: (
                <>
                    <SelectPlayerSection
                        handleBack={handleBack}
                        handleNext={handleNext}
                        playersList={playersList}
                        playerInContext={playerInContext}
                        playerSelectedIdChanged={playerSelectedIdChanged}
                        playerId={player?.id}
                    />
                </>
            ),
        },
        {
            label: constants.ADD_GAME.SELECT_COURT,
            description: (
                <>
                    <SelectcourtSection
                        handleBack={handleBack}
                        handleNext={handleNext}
                        courts={courts}
                        courtSelectedIdChanged={courtSelectedIdChanged}
                        courtId={courtId}
                    />
                </>
            ),
        },
        {
            label: constants.ADD_GAME.SELECT_DATE,
            description: (
                <>
                    <SelectDateSection handleBack={handleBack} handleNext={handleNext} date={date} dateSelectedChanged={dateSelectedChanged} />
                </>
            ),
        },
        {
            label: constants.ADD_GAME.MATCH_SCORE,
            description: (
                <>
                    <MatchScoreSection
                        handleBack={handleBack}
                        handleNext={handleNext}
                        playerOne={player}
                        playerInContext={playerInContext}
                        playerOneScore={playerOneScore}
                        playerTwoScore={playerTwoScore}
                        matchScoreChanged={matchScoreChanged}
                    />
                </>
            ),
        },
        {
            label: constants.ADD_GAME.SET_SCORE,
            description: (
                <>
                    <SetScoreSection
                        playerOne={player}
                        handleBack={handleBack}
                        handleFinish={handleFinish}
                        playerInContext={playerInContext}
                        playerTwoScore={playerTwoScore}
                        playerOneScore={playerOneScore}
                    />
                </>
            ),
        },
    ];

    return (
        <>
            <div className={`add-match-form-container ${isLoading ? 'wizard-steps-blur-background' : ''}`}>
                {isGuest ? (
                    <div>
                        <div>
                            <span>{constants.AUTH.ADD_MATCH_NOT_AVAILABLE}</span>
                        </div>
                        <div>
                            <span>{constants.AUTH.IS_GUEST}</span>
                        </div>
                    </div>
                ) : (
                    <CacheProvider value={rtlCache}>
                        <ThemeProvider theme={rtlTheme}>
                            <Stepper activeStep={activeStep} orientation='vertical' dir='rtl'>
                                {steps.map((step, index) => {
                                    const summary = getStepSummary(index);
                                    return (
                                        <Step key={step.label}>
                                            <StepLabel
                                                optional={
                                                    summary ? (
                                                        <span className='step-summary-label'>{summary}</span>
                                                    ) : null
                                                }
                                            >
                                                {step.label}
                                            </StepLabel>
                                            <StepContent>{step.description}</StepContent>
                                        </Step>
                                    );
                                })}
                                {isFinished && (
                                    <div className={!error ? 'success-game-row row' : 'failure-game-row row'}>
                                        <div className='col-12'>
                                            <span>{!error ? constants.ADD_GAME.SUCCESS_MESSAGE : constants.ADD_GAME.ADD_GAME_FAILED}</span>
                                        </div>
                                        <div className='col-12'>
                                            <span>{!error ? constants.ADD_GAME.MORE_DETAILS : constants.ADD_GAME.TRY_AGAIN_LATER}</span>
                                        </div>
                                    </div>
                                )}
                            </Stepper>
                        </ThemeProvider>
                    </CacheProvider>
                )}
            </div>
        </>
    );
}

export default WizardSteps;
